import * as yup from 'yup';
import { PropertyFormData, PropertyType, ListingType, ClientFormData, ClientSource, ClientStatus, ClientPreferences } from '@/types';

// Property form validation schema
export const propertyFormSchema: yup.ObjectSchema<PropertyFormData> = yup.object({
  metadata: yup.object({
    title: yup.string().required('Property title is required').min(5, 'Title must be at least 5 characters').max(200, 'Title must not exceed 200 characters'),
    description: yup.string().required('Description is required').min(20, 'Description must be at least 20 characters').max(5000, 'Description must not exceed 5000 characters'),
    price: yup.number().required('Price is required').positive('Price must be positive').min(0, 'Price cannot be negative'),
    currency: yup.string().required('Currency is required').default('USD'),
    propertyType: yup.string().oneOf(Object.values(PropertyType), 'Invalid property type').required('Property type is required'),
    listingType: yup.string().oneOf(Object.values(ListingType), 'Invalid listing type').required('Listing type is required'),
    bedrooms: yup.number().required('Number of bedrooms is required').integer('Bedrooms must be a whole number').min(0, 'Bedrooms cannot be negative').max(50, 'Bedrooms cannot exceed 50'),
    bathrooms: yup.number().required('Number of bathrooms is required').min(0, 'Bathrooms cannot be negative').max(50, 'Bathrooms cannot exceed 50'),
    squareFootage: yup.number().required('Square footage is required').positive('Square footage must be positive').min(1, 'Square footage must be at least 1'),
    lotSize: yup.number().optional().min(0, 'Lot size cannot be negative'),
    yearBuilt: yup.number().optional().integer('Year must be a whole number').min(1800, 'Year must be after 1800').max(new Date().getFullYear() + 1, 'Year cannot be in the future'),
    parkingSpaces: yup.number().optional().integer('Parking spaces must be a whole number').min(0, 'Parking spaces cannot be negative'),
    petFriendly: yup.boolean().optional(),
    furnished: yup.boolean().optional(),
    utilities: yup.array().of(yup.string()).optional(),
    amenities: yup.array().of(yup.string()).optional(),
    images: yup.array().of(yup.string()).optional(),
    documents: yup.array().of(yup.string()).optional(),
    virtualTour: yup.string().url('Virtual tour must be a valid URL').optional(),
    videoUrl: yup.string().url('Video URL must be a valid URL').optional(),
  }).required(),
  features: yup.object({
    heating: yup.array().of(yup.string()).optional(),
    cooling: yup.array().of(yup.string()).optional(),
    flooring: yup.array().of(yup.string()).optional(),
    appliances: yup.array().of(yup.string()).optional(),
    security: yup.array().of(yup.string()).optional(),
    outdoor: yup.array().of(yup.string()).optional(),
    accessibility: yup.array().of(yup.string()).optional(),
    energyEfficient: yup.boolean().optional(),
    smartHome: yup.boolean().optional(),
    pool: yup.boolean().optional(),
    garden: yup.boolean().optional(),
    balcony: yup.boolean().optional(),
    fireplace: yup.boolean().optional(),
    basement: yup.boolean().optional(),
    attic: yup.boolean().optional(),
    garage: yup.boolean().optional(),
  }).required(),
  location: yup.object({
    latitude: yup.number().required('Latitude is required').min(-90, 'Latitude must be between -90 and 90').max(90, 'Latitude must be between -90 and 90'),
    longitude: yup.number().required('Longitude is required').min(-180, 'Longitude must be between -180 and 180').max(180, 'Longitude must be between -180 and 180'),
    address: yup.string().required('Address is required').min(5, 'Address must be at least 5 characters'),
    city: yup.string().required('City is required').min(2, 'City must be at least 2 characters'),
    state: yup.string().required('State is required').min(2, 'State must be at least 2 characters'),
    zipCode: yup.string().required('Zip code is required').matches(/^\d{5}(-\d{4})?$/, 'Invalid zip code format'),
    country: yup.string().required('Country is required').min(2, 'Country must be at least 2 characters'),
    neighborhood: yup.string().optional(),
    schoolDistrict: yup.string().optional(),
    walkScore: yup.number().optional().min(0, 'Walk score must be between 0 and 100').max(100, 'Walk score must be between 0 and 100'),
    transitScore: yup.number().optional().min(0, 'Transit score must be between 0 and 100').max(100, 'Transit score must be between 0 and 100'),
  }).required(),
  tags: yup.array().of(yup.string()).optional(),
  isFeatured: yup.boolean().optional().default(false),
  isActive: yup.boolean().optional().default(true),
  listingDate: yup.date().required('Listing date is required'),
  expirationDate: yup.date().optional().when('listingDate', {
    is: (listingDate: Date) => listingDate,
    then: (schema) => schema.min(yup.ref('listingDate'), 'Expiration date must be after listing date'),
  }),
}) as yup.ObjectSchema<PropertyFormData>;

// Client preferences validation schema
export const clientPreferencesSchema: yup.ObjectSchema<ClientPreferences> = yup.object({
  priceRange: yup.object({
    min: yup.number().required('Minimum price is required').min(0, 'Minimum price cannot be negative'),
    max: yup.number().required('Maximum price is required').min(yup.ref('min'), 'Maximum price must be greater than minimum price'),
    currency: yup.string().required('Currency is required').default('USD'),
  }).required(),
  budget: yup.object({
    min: yup.number().required().min(0),
    max: yup.number().required().min(yup.ref('min')),
    currency: yup.string().required(),
  }).optional().default(undefined),
  propertyTypes: yup.array().of(yup.string().oneOf(Object.values(PropertyType)).required()).required().default([]),
  listingTypes: yup.array().of(yup.string().oneOf(Object.values(ListingType)).required()).required().default([]),
  location: yup.object({
    cities: yup.array().of(yup.string().required()).required().default([]),
    neighborhoods: yup.array().of(yup.string().required()).required().default([]),
    maxDistanceFromWork: yup.number().optional().min(0, 'Distance cannot be negative'),
    schoolDistricts: yup.array().of(yup.string().required()).optional(),
    walkScore: yup.number().optional().min(0, 'Walk score must be between 0 and 100').max(100, 'Walk score must be between 0 and 100'),
    transitScore: yup.number().optional().min(0, 'Transit score must be between 0 and 100').max(100, 'Transit score must be between 0 and 100'),
  }).required(),
  features: yup.object({
    bedrooms: yup.object({
      min: yup.number().required('Minimum bedrooms is required').integer('Bedrooms must be a whole number').min(0, 'Bedrooms cannot be negative'),
      max: yup.number().required('Maximum bedrooms is required').integer('Bedrooms must be a whole number').min(yup.ref('min'), 'Maximum bedrooms must be greater than or equal to minimum'),
    }).required(),
    bathrooms: yup.object({
      min: yup.number().required('Minimum bathrooms is required').min(0, 'Bathrooms cannot be negative'),
      max: yup.number().required('Maximum bathrooms is required').min(yup.ref('min'), 'Maximum bathrooms must be greater than or equal to minimum'),
    }).required(),
    squareFootage: yup.object({
      min: yup.number().required('Minimum square footage is required').min(0, 'Square footage cannot be negative'),
      max: yup.number().required('Maximum square footage is required').min(yup.ref('min'), 'Maximum square footage must be greater than or equal to minimum'),
    }).required(),
    amenities: yup.array().of(yup.string().required()).required().default([]),
    mustHaves: yup.array().of(yup.string().required()).required().default([]),
    dealBreakers: yup.array().of(yup.string().required()).required().default([]),
  }).required(),
  timeline: yup.object({
    moveInDate: yup.date().optional().min(new Date(), 'Move-in date cannot be in the past'),
    urgency: yup.string().oneOf(['low', 'medium', 'high']).required('Urgency is required').default('medium'),
    flexibility: yup.string().oneOf(['low', 'medium', 'high']).required('Flexibility is required').default('medium'),
  }).required(),
  financing: yup.object({
    preApproved: yup.boolean().required('Pre-approval status is required').default(false),
    downPayment: yup.number().required('Down payment is required').min(0, 'Down payment cannot be negative').default(0),
    loanType: yup.string().optional(),
    creditScore: yup.number().optional().min(300, 'Credit score must be between 300 and 850').max(850, 'Credit score must be between 300 and 850'),
  }).required(),
}) as yup.ObjectSchema<ClientPreferences>;

// Client form validation schema
export const clientFormSchema: yup.ObjectSchema<ClientFormData> = yup.object({
  profile: yup.object({
    firstName: yup.string().required('First name is required').min(2, 'First name must be at least 2 characters'),
    lastName: yup.string().required('Last name is required').min(2, 'Last name must be at least 2 characters'),
    email: yup.string().email('Invalid email address').required('Email is required'),
    phone: yup.string().required('Phone number is required').matches(/^\+?[\d\s\-()]+$/, 'Invalid phone number format'),
    avatar: yup.string().url('Avatar must be a valid URL').optional(),
    address: yup.object({
      street: yup.string().optional(),
      city: yup.string().optional(),
      state: yup.string().optional(),
      zipCode: yup.string().optional(),
      country: yup.string().optional(),
    }).optional(),
    dateOfBirth: yup.date().optional().max(new Date(), 'Date of birth cannot be in the future'),
    occupation: yup.string().optional(),
    income: yup.number().optional().min(0, 'Income cannot be negative'),
    familySize: yup.number().optional().integer('Family size must be a whole number').min(1, 'Family size must be at least 1'),
    pets: yup.array().of(yup.string()).optional(),
    lifestyle: yup.array().of(yup.string()).optional(),
  }).required(),
  preferences: clientPreferencesSchema,
  matchingProfile: yup.object({
    behavior: yup.object({
      searchFrequency: yup.string().oneOf(['daily', 'weekly', 'monthly']).optional(),
      responseTime: yup.string().oneOf(['immediate', 'within_hour', 'within_day', 'within_week']).optional(),
      engagementLevel: yup.string().oneOf(['low', 'medium', 'high']).optional(),
      preferredContactMethod: yup.string().oneOf(['email', 'phone', 'text', 'app']).optional(),
    }).optional(),
    communication: yup.object({
      language: yup.string().optional(),
      timezone: yup.string().optional(),
      preferredTimes: yup.array().of(yup.string()).optional(),
      communicationStyle: yup.string().oneOf(['formal', 'casual', 'mixed']).optional(),
    }).optional(),
    priority: yup.object({
      price: yup.number().optional().min(0, 'Priority must be between 0 and 10').max(10, 'Priority must be between 0 and 10'),
      location: yup.number().optional().min(0, 'Priority must be between 0 and 10').max(10, 'Priority must be between 0 and 10'),
      features: yup.number().optional().min(0, 'Priority must be between 0 and 10').max(10, 'Priority must be between 0 and 10'),
      timeline: yup.number().optional().min(0, 'Priority must be between 0 and 10').max(10, 'Priority must be between 0 and 10'),
      quality: yup.number().optional().min(0, 'Priority must be between 0 and 10').max(10, 'Priority must be between 0 and 10'),
    }).optional(),
  }).required(),
  source: yup.string().oneOf(Object.values(ClientSource), 'Invalid client source').required('Client source is required'),
  status: yup.string().oneOf(Object.values(ClientStatus), 'Invalid client status').required('Client status is required'),
  tags: yup.array().of(yup.string()).optional(),
  notes: yup.string().optional(),
}) as yup.ObjectSchema<ClientFormData>;
